import { useCallback, useEffect, useState } from 'react';
import { guid } from '../../../domain/common';
import ArrowIcon from '../../../ui/svg/common/arrow';
import { IDropDownOption, IDropDownProps } from './interfaces';

/**
 * drop-down component
 */
const DropDown = (props: IDropDownProps) => {

    const [ isOpened, setIsOpened ] = useState(false);
    const [ id ] = useState(guid());
    const width = props.width || '220px';
    const maxHeight = props.maxHeight || '300px';
    const selectedOption = props.options.find(option => option.value === props.selectedValue);
    const zIndex = props.zIndex === undefined ? 10 : props.zIndex;

    /**
     * handle option selection
     */
    const selectOption = (option: IDropDownOption) => {
        props.onSelect(option);
        close();
    };

    /**
     * open drop-down
     */
    const open = () => {
        setIsOpened(true);
    };

    /**
     * close drop-down
     */
    const close = useCallback(() => {
        setIsOpened(false);
    }, []);

    /**
     * toggle drop-down
     */
    const toggle = () => {

        if(isOpened){
            close();
        }
        else{
            open();
        }
    };

    /**
     * handle escape key
     */
    const handleEscape = useCallback(async (evt: any) => {
        if(evt.key === 'Escape') {
            close();
        }
    }, [close]);

    /**
     * handle click outside
     */
    useEffect(() => {

        document.addEventListener('click', close);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('click', close);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [close, handleEscape]);

    return (
        <div
            className="w-drop-down inline-flex flex-col align-middle relative cursor-pointer"
            style={{
                width,
                zIndex,
            }
        }>
            <button
                type="button"
                className={`w-full whitespace-nowrap inline-flex items-center outline-none text-left ${ props.panelClasses } `}
                data-toggle="dropdown"
                role="combobox"
                aria-owns={ id }
                aria-haspopup="listbox"
                aria-expanded="false"
                title={ props.title }
                onClick={ (evt => {
                    evt.preventDefault();
                    evt.stopPropagation();
                    toggle();
                }) }>

                    <div className="flex-1">{ selectedOption ? selectedOption.text : '&nbsp;' }</div>

                    <ArrowIcon
                        width="12px"
                        color="#4a4a4a"
                        title="Arrow Down"
                    />
            </button>

            {
                isOpened &&
                <div className="w-drop-down__content absolute top-full left-0 overflow-hidden w-full bg-white px-4 pt-4 pb-2"
                     style={{
                         maxHeight,
                     }}>
                    <div
                        className="w-full h-full overflow-y-auto flex"
                        role="listbox"
                        id={ id }
                        tabIndex={ -1 }
                        aria-activedescendant="bs-select-1-0">

                        <ul
                            className="flex flex-col w-full"
                            role="presentation">

                            {
                                props.options.map((option, index) => {
                                    const isSelected = option.value === props.selectedValue;

                                    return (
                                        <li
                                            key={ option.key }
                                            className={`mb-2 w-full ${ isSelected ? 'w-drop-down__selected-option' : '' }`}
                                            data-selected={ isSelected }>
                                            {
                                                option.link &&
                                                <a
                                                    role="option"
                                                    href={ option.link }
                                                    target={ option.target }
                                                    className="no-underline text-gray-500 outline-none inline-block w-full"
                                                    id={`${ id }-${ index }`}
                                                    tabIndex={ 0 }
                                                    aria-setsize={ props.options.length }
                                                    aria-posinset={ index + 1 }
                                                    aria-selected="true">{ option.text }</a>
                                            }

                                            {
                                                !option.link &&
                                                <button
                                                    type="button"
                                                    className="text-gray-500 outline-none focus:underline inline-block w-full"
                                                    id={`${ id }-${ index }`}
                                                    tabIndex={ 0 }
                                                    onClick={ evt => {
                                                        evt.preventDefault();
                                                        evt.stopPropagation();
                                                        selectOption(option);
                                                    }
                                                }>{ option.text }</button>
                                            }
                                        </li>
                                    )
                                })
                            }
                        </ul>
                    </div>
                </div>
            }
        </div>
    )
};

export default DropDown;